import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { Customer } from "@app/customer/model/customer";
import { Entity } from "@app/entity/model/entity";
import { ClareServicePlan, ClareServiceSubscription, Project } from "@app/project/model";
import { SERVICE_PLAN_DIALOG_VIEW } from "@app/project/model/service-plan";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { onlineClareStatus } from "@app/shared/model/clare-status.model";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { ClareProcessStep, activeStep } from "@app/shared/model/utility.model";
import { User } from "@app/user/model/user";
import { Address } from "ngx-google-places-autocomplete/objects/address";
import { Subject, filter, take, takeUntil } from "rxjs";

@Component({
    selector: "clare-account-incomplete",
    templateUrl: "./account-incomplete.component.html",
    styleUrls: ["./account-incomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountIncompleteComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isCompletingProcess = false;
    @Input() project: Project;
    @Input() entity: Entity;
    @Input() user: Customer;
    @Input() address: Address;
    @Input() isClareOne = false;
    @Input() selectedPlan: ClareServicePlan;
    @Input() subscription: ClareServiceSubscription;

    @Output() completeUpdate = new EventEmitter();
    @Output() spUpdated = new EventEmitter();

    steps: ClareProcessStep[] = [
        {
            id: 0,
            text: "Create Account",
            description: "Includes address, contact and system info.",
            isActive: true,
            isCompleted: false,
        },
        {
            id: 1,
            text: "Activate Account",
            description: "Choose a plan and activate selected services.",
            isActive: false,
            isCompleted: false,
        },
    ];

    isServicePlanSelected = false;
    uuid = "";

    initializedData: {
        project?: Project;
        user?: User;
        address?: Address;
        currentPlan?: unknown;
        selectedPlan?: unknown;
        subscription?: unknown;
        spView?: SERVICE_PLAN_DIALOG_VIEW;
    } = {
        spView: SERVICE_PLAN_DIALOG_VIEW.SELECT_PLAN,
    };
    isProjectOnline = false;

    private unsubscribe$ = new Subject();

    constructor(
        private readonly servicePlanService: ServicePlansService,
        private projectDispatchers: ProjectDispatchers,
        private cdr: ChangeDetectorRef
    ) {}
    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    ngOnChanges(changes: NgChanges<AccountIncompleteComponent>): void {
        if (changes.address?.currentValue) {
            this.updateInitializedData("address", changes.address.currentValue);
        }

        if (changes.subscription?.currentValue) {
            this.updateInitializedData("subscription", changes.subscription.currentValue);
        }

        if (changes.selectedPlan?.currentValue) {
            this.updateInitializedData("selectedPlan", changes.selectedPlan.currentValue);
            this.isServicePlanSelected = true;
        }

        if (changes.project?.currentValue) {
            const project = changes.project?.currentValue;
            this.uuid = ProjectHelperService.getProjectUuid(project);
            this.updateInitializedData("project", project);
            this.updateSPStep(project);
        }

        if (changes.user?.currentValue) {
            this.updateInitializedData("user", changes.user.currentValue);
            this.user = changes.user.currentValue;
            if (this.user?.id && this.project?.claresystem?.id) {
                this.onAccountCompleteUpdate();
            }
        }
    }

    private updateSPStep(project: Project) {
        if (!this.selectedPlan) {
            this.servicePlanService
                .getActiveServicePlans(project.grandfathered)
                .pipe(
                    filter(plans => !!plans.length),
                    take(1)
                )
                .subscribe(plans => {
                    const selectedPlan = this.servicePlanService.getBasicPlan(plans);
                    this.updateInitializedData("selectedPlan", selectedPlan);
                });
        }
    }

    ngOnInit(): void {
        this.projectDispatchers
            .selectActiveProjectStatus$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(status => {
                this.isProjectOnline = status === onlineClareStatus;
            });
    }

    onAccountCompleteUpdate() {
        activeStep(this.steps, 1, 0);
    }

    onAccountCompleteActivate(selectedPlan: ClareServicePlan) {
        this.completeUpdate.emit(selectedPlan);
    }

    onSPCompleteUpdate(eventData) {
        this.spUpdated.emit(eventData);
    }

    private updateInitializedData(key: string, updatedData) {
        this.initializedData = {
            ...this.initializedData,
            [key]: updatedData,
        };
    }

    onTabSelectionChange(tabIndex: number) {
        if (tabIndex > 0 && !this.steps[tabIndex - 1].isCompleted) {
            return;
        }
        this.steps.forEach((step, index) => {
            if (index === tabIndex) {
                step.isActive = true;
            } else {
                step.isActive = false;
            }
        });
    }

    onNavigateEditUser() {
        activeStep(this.steps, 0);
    }
}
